import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Bus } from '../../types';
import BusForm from './BusForm';
import BusDetails from './BusDetails';

interface BusFilters {
  search: string;
  status: string;
  isActive: string;
}

const Buses: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<BusFilters>({
    search: '',
    status: '',
    isActive: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showBusForm, setShowBusForm] = useState(false);
  const [showBusDetails, setShowBusDetails] = useState(false);
  const [selectedBus, setSelectedBus] = useState<Bus | null>(null);
  const [selectedBusId, setSelectedBusId] = useState<string | null>(null);
  const [notifyBusyId, setNotifyBusyId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Fetch buses with React Query
  const { 
    data: busesData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['buses', currentPage, filters],
    queryFn: () => apiService.getBuses(currentPage, 20, filters),
    staleTime: 30000, // 30 seconds
  });

  const buses = busesData?.data || [];
  const pagination = busesData?.pagination;

  // Delete bus mutation
  const deleteBusMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteBus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buses'] });
    },
  });

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof BusFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleDeleteBus = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الحافلة؟')) {
      try {
        await deleteBusMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting bus:', error);
        alert('حدث خطأ أثناء حذف الحافلة');
      }
    }
  };

  const handleCreateBus = () => {
    setSelectedBus(null);
    setShowBusForm(true);
  };

  const handleEditBus = (bus: Bus) => {
    setSelectedBus(bus);
    setShowBusForm(true);
  };

  const handleViewBus = (busId: string) => {
    setSelectedBusId(busId);
    setShowBusDetails(true);
  };

  const handleBusFormClose = () => {
    setShowBusForm(false);
    setSelectedBus(null);
  };

  const handleBusDetailsClose = () => {
    setShowBusDetails(false);
    setSelectedBusId(null);
  };

  const handleBusSuccess = (bus: Bus) => {
    console.log('Bus saved successfully:', bus);
  };

  const handleNotifyDriver = async (bus: Bus) => {
    if (!bus.driver?.phone) {
      alert('لا يوجد جوال مسجّل لهذا السائق');
      return;
    }
    setNotifyBusyId(bus._id);
    try {
      const res = await apiService.notifyBusDriver(bus._id);
      alert(res.message || 'تم إرسال واتساب للسائق');
    } catch (err: any) {
      alert(
        err?.response?.data?.message || err?.message || 'فشل إرسال واتساب للسائق'
      );
    } finally {
      setNotifyBusyId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'available': { label: 'متاح', color: 'bg-green-100 text-green-800' },
      'on-trip': { label: 'في رحلة', color: 'bg-blue-100 text-blue-800' },
      'maintenance': { label: 'صيانة', color: 'bg-yellow-100 text-yellow-800' },
      'out-of-service': { label: 'خارج الخدمة', color: 'bg-red-100 text-red-800' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">خطأ في تحميل البيانات</h3>
          <p className="text-red-600 mt-1">حدث خطأ أثناء تحميل بيانات الحافلات</p>
          <button 
            onClick={() => refetch()}
            className="mt-3 bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة الحافلات</h1>
          <p className="mt-1 text-sm text-gray-500">
            إجمالي الحافلات: {pagination?.total || 0}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button onClick={handleCreateBus} className="btn-primary">
            <PlusIcon className="w-5 h-5 ml-2" />
            حافلة جديدة
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الحافلة، اللوحة، أو اسم السائق..."
                className="input-field pr-10"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="w-5 h-5 ml-2" />
            فلترة
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حالة الحافلة
                </label>
                <select
                  className="input-field"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="available">متاح</option>
                  <option value="on-trip">في رحلة</option>
                  <option value="maintenance">صيانة</option>
                  <option value="out-of-service">خارج الخدمة</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  حالة التفعيل
                </label>
                <select
                  className="input-field"
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                >
                  <option value="">جميع الحافلات</option>
                  <option value="true">نشط</option>
                  <option value="false">غير نشط</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Buses Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري تحميل الحافلات...</p>
          </div>
        ) : buses.length === 0 ? (
          <div className="p-8 text-center">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد حافلات</h3>
            <p className="mt-1 text-sm text-gray-500">ابدأ بإضافة حافلة جديدة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    رقم الحافلة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    رقم اللوحة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    السائق
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    السعة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {buses.map((bus) => (
                  <tr key={bus._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bus.busNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        {bus.model?.brand} {bus.model?.model}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bus.plateNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bus.driver.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {bus.driver.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bus.capacity} مقعد
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(bus.status)}
                      {!bus.isActive && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            غير نشط
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          title="عرض التفاصيل"
                          onClick={() => handleViewBus(bus._id)}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {bus.driver?.phone && (
                          <button
                            className="text-emerald-600 hover:text-emerald-900 disabled:opacity-50"
                            title="واتساب السائق"
                            disabled={notifyBusyId === bus._id}
                            onClick={() => handleNotifyDriver(bus)}
                          >
                            <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          className="text-green-600 hover:text-green-900"
                          title="تعديل"
                          onClick={() => handleEditBus(bus)}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          title="حذف"
                          onClick={() => handleDeleteBus(bus._id)}
                          disabled={deleteBusMutation.isPending}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bus Form Modal */}
      <BusForm
        bus={selectedBus || undefined}
        isOpen={showBusForm}
        onClose={handleBusFormClose}
        onSuccess={handleBusSuccess}
      />

      {/* Bus Details Modal */}
      {selectedBusId && (
        <BusDetails
          busId={selectedBusId}
          isOpen={showBusDetails}
          onClose={handleBusDetailsClose}
          onEdit={handleEditBus}
        />
      )}
    </div>
  );
};

export default Buses; 